import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, type KeyboardEvent, type ReactNode, useState } from "react";

import { cn } from "../../lib/utils";

/** A single column definition: a unique key, its already-translated header label, and how to render a row's cell. */
export interface AuditTableColumn<Row> {
  key: string;
  header: string;
  className?: string;
  render: (row: Row) => ReactNode;
}

interface AuditTableProperties<Row> {
  /** sr-only `<caption>` text — already translated by the caller. */
  caption: string;
  columns: Array<AuditTableColumn<Row>>;
  rows: Row[];
  /**
   * When provided, rows expand accordion-style (one open at a time) to reveal
   * this content. Return `null`/`undefined` for a given row to mark it
   * non-expandable — it renders with no chevron and isn't clickable, while
   * the leading chevron column still lines up across every row.
   */
  renderExpandedRow?: (row: Row) => ReactNode;
  /**
   * When true, the column headers stick to the top of this table's own scroll
   * container — NOT the page/tab's scroll container. Requires an ancestor with
   * a bounded height (e.g. `flex-1 min-h-0`) for the internal scroll area to
   * take effect; without one, the table just grows unbounded and this has no
   * visible effect.
   */
  stickyHeader?: boolean;
}

// Shared bordered table shell for the Internal Audit drawer tabs
function AuditTable<Row>({ caption, columns, rows, renderExpandedRow, stickyHeader }: AuditTableProperties<Row>) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  /**
   * Handles toggling the expanded state of a row when clicked or activated via keyboard.
   */
  const toggleRow = (index: number) => {
    setExpandedIndex((current) => (current === index ? null : index));
  };
  const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, index: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleRow(index);
    }
  };

  return (
    <div className="border-(--color-border-default-muted) text-primary flex max-h-full min-h-0 w-full flex-col border">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full">
          {/* For screen reader accessibility */}
          <caption className="sr-only">{caption}</caption>

          {/* Columns */}
          <thead className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) uppercase tracking-[0.4px]">
            <tr>
              {renderExpandedRow && (
                <th
                  scope="col"
                  className={cn("bg-(--color-background-default-muted) w-8", stickyHeader && "sticky top-0 z-10")}
                />
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    "bg-(--color-background-default-muted) border-(--color-border-default-muted) px-(--spacing-4) py-(--spacing-3) text-left",
                    stickyHeader && "sticky top-0 z-10"
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          {/* Rows */}
          <tbody className="text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height)">
            {rows.map((row, index) => {
              const expandedContent = renderExpandedRow?.(row) ?? null;

              // States
              const isExpandable = expandedContent !== null;
              const isExpanded = isExpandable && expandedIndex === index;

              // Extra props to make the row accessible and interactive if it's expandable
              const extraProperties = isExpandable
                ? {
                    role: "button",
                    tabIndex: 0,
                    "aria-expanded": isExpanded,
                    onClick: () => toggleRow(index),
                    onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => handleRowKeyDown(event, index),
                  }
                : {};

              return (
                <Fragment key={index}>
                  <tr
                    className={cn(
                      "border-(--color-border-default-muted) bg-(--color-background-default-base) border-t",
                      isExpandable && "hover:bg-(--colors-background-default-accent-light) cursor-pointer",
                      isExpanded && "bg-(--colors-background-default-accent-light)"
                    )}
                    {...extraProperties}
                  >
                    {/* Expanded row toggle cell */}
                    {renderExpandedRow && (
                      <td className="px-(--spacing-4) py-(--spacing-3)">
                        {isExpandable &&
                          (isExpanded ? (
                            <ChevronDown className="size-4" aria-hidden="true" />
                          ) : (
                            <ChevronRight className="size-4" aria-hidden="true" />
                          ))}
                      </td>
                    )}

                    {/* Data cells for the row */}
                    {columns.map((column) => (
                      <td key={column.key} className={cn("px-(--spacing-4) py-(--spacing-3)", column.className)}>
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>

                  {/* Expanded content row */}
                  {isExpanded && (
                    <tr className="border-(--color-border-default-muted) bg-(--color-background-default-base) border-t">
                      <td className="px-(--spacing-4) py-(--spacing-3)" colSpan={columns.length + 1}>
                        {expandedContent}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AuditTable;
